import {DataEditorProps} from "@glideapps/glide-data-grid";
import {useCallback, useMemo} from "react";
import {
  ColumnDefinition,
  ColumnLayoutOptions,
  resolveColumnLayout,
  useColumnLayout,
} from "../column-management/useColumnLayout";
import {DataGridContentColumn} from "./data-grid-types";

export type DataGridColumnDefinition<T> = DataGridContentColumn<T> & ColumnDefinition;
export type {
  ColumnState as DataGridColumnState,
  ColumnLayout as DataGridColumnLayout,
  ColumnLayoutControls as DataGridColumnLayoutControls,
} from "../column-management/useColumnLayout";
export function resolveDataGridColumnLayout<T>(columns: readonly DataGridColumnDefinition<T>[], saved: unknown) {
  return resolveColumnLayout(columns, saved);
}

/** Glide adapter. Storage keys and the version-1 payload remain compatible with existing ticket layouts. */
export function useDataGridColumnLayout<T>(
  columns: readonly DataGridColumnDefinition<T>[],
  storageKey: string,
  options?: ColumnLayoutOptions
) {
  const managed = useColumnLayout(columns, storageKey, options);
  const orderedColumns = useMemo(
    () => managed.columns.map((column) => ({...column, width: column.width ?? 100})),
    [managed.columns]
  );
  const visibleColumns = useMemo(
    () =>
      orderedColumns
        .filter((column) => column.visible)
        .map((column) => {
          const nativeColumn: DataGridColumnDefinition<T> & {width: number; visible?: boolean} = {...column};
          delete nativeColumn.visible;
          delete nativeColumn.defaultVisible;
          delete nativeColumn.columnLabel;
          return nativeColumn;
        }),
    [orderedColumns]
  );
  const onColumnResize = useCallback<NonNullable<DataEditorProps["onColumnResize"]>>(
    (column, width) => {
      if (column.id) managed.setColumnWidth(column.id, width);
    },
    [managed.setColumnWidth]
  );
  const columnSort = useMemo(() => ({value: managed.sort, onChange: managed.setSort}), [managed.sort, managed.setSort]);
  return {...managed, columns: orderedColumns, visibleColumns, onColumnResize, columnSort};
}
